import {
    type AuditLogFilters,
    isChangedField,
    isEntryId,
    isIpAddress,
    isMetadataPath,
    isRouteFilter,
    isStorableText,
    AUDIT_FILTER_MAX_LENGTH as MAX,
    parseAuditId,
    parseStatusCode,
} from '@/domain/auditLog';

/** The filters a user types, as opposed to picks. */
export type AuditDraftKey =
    | 'entryId'
    | 'actorEmail'
    | 'actorUserId'
    | 'impersonatorEmail'
    | 'action'
    | 'entityId'
    | 'entityLabel'
    | 'statusCode'
    | 'requestId'
    | 'ip'
    | 'userAgent'
    | 'route'
    | 'changedField'
    | 'changedFrom'
    | 'changedTo'
    | 'metadataPath'
    | 'metadataValue';

type NumberDraftKey = 'actorUserId' | 'statusCode';
type TextDraftKey = Exclude<AuditDraftKey, NumberDraftKey>;

/** What a field's text means as a filter. */
export interface DraftReading {
    /** Undefined removes the filter. */
    value: string | number | undefined;
    /** There is text, but it cannot filter, so the field says it is ignored. */
    invalid: boolean;
}

// The input's maxLength counts UTF-16 units and the backend counts characters, so text the
// input accepts always passes the backend's bound. Ids, codes and patterned fields have none:
// cutting a pasted id short could silently filter on a different one, so an overlong value
// is marked as ignored instead.
const MAX_LENGTH: Readonly<Partial<Record<AuditDraftKey, number>>> = {
    actorEmail: MAX.actorEmail,
    impersonatorEmail: MAX.impersonatorEmail,
    action: MAX.action,
    entityId: MAX.entityId,
    entityLabel: MAX.entityLabel,
    requestId: MAX.requestId,
    userAgent: MAX.userAgent,
    route: MAX.route,
    changedFrom: MAX.value,
    changedTo: MAX.value,
    metadataValue: MAX.value,
};

export function draftMaxLength(key: AuditDraftKey): number | undefined {
    return MAX_LENGTH[key];
}

/** Fields whose text must match a pattern, and the message that says it does not. */
const INVALID_MESSAGE: Readonly<Partial<Record<AuditDraftKey, string>>> = {
    entryId: 'auditLog.filters.invalidEntryId',
    actorUserId: 'auditLog.filters.invalidId',
    statusCode: 'auditLog.filters.invalidStatusCode',
    ip: 'auditLog.filters.invalidIp',
    changedField: 'auditLog.filters.invalidField',
    metadataPath: 'auditLog.filters.invalidPath',
};

const PATTERNS: Readonly<Partial<Record<TextDraftKey, (text: string) => boolean>>> = {
    entryId: isEntryId,
    ip: isIpAddress,
    changedField: isChangedField,
    metadataPath: isMetadataPath,
    route: isRouteFilter,
};

// Matched exactly against a recorded value, so the spaces typed are part of the value.
const EXACT_KEYS: readonly AuditDraftKey[] = ['changedFrom', 'changedTo', 'metadataValue'];

/** Only the id and code fields want a numeric keyboard. */
export const NUMERIC_DRAFT_KEYS: readonly AuditDraftKey[] = [
    'entryId',
    'actorUserId',
    'statusCode',
];

function isNumberKey(key: AuditDraftKey): key is NumberDraftKey {
    return key === 'actorUserId' || key === 'statusCode';
}

function readNumber(key: NumberDraftKey, draft: string): number | undefined {
    return key === 'statusCode' ? parseStatusCode(draft) : parseAuditId(draft);
}

function readText(key: TextDraftKey, draft: string): string | undefined {
    const text = EXACT_KEYS.includes(key) ? draft : draft.trim();
    if (text === '' || !isStorableText(text)) return undefined;
    const pattern = PATTERNS[key];
    return pattern === undefined || pattern(text) ? text : undefined;
}

export function readDraft(key: AuditDraftKey, draft: string): DraftReading {
    const value = isNumberKey(key) ? readNumber(key, draft) : readText(key, draft);
    const blank = EXACT_KEYS.includes(key) ? draft === '' : draft.trim() === '';
    return { value, invalid: !blank && value === undefined };
}

export function draftInvalidMessage(key: AuditDraftKey): string {
    return INVALID_MESSAGE[key] ?? 'auditLog.filters.invalidText';
}

/** The field's text for the filter as it applies now. */
export function draftOf(key: AuditDraftKey, filters: AuditLogFilters): string {
    const value = filters[key];
    return value === undefined ? '' : String(value);
}

/** The text the URL will hold once `draft` is applied, so a field can tell its own echo. */
export function appliedText(key: AuditDraftKey, draft: string): string {
    const { value } = readDraft(key, draft);
    return value === undefined ? '' : String(value);
}

/** Whether `draft` means the filter `applied` already holds. */
export function isDraftApplied(key: AuditDraftKey, draft: string, applied: string): boolean {
    return readDraft(key, draft).value === readDraft(key, applied).value;
}

/** The filters with one field's text applied; text that cannot filter removes the filter. */
export function withDraft(
    filters: AuditLogFilters,
    key: AuditDraftKey,
    draft: string
): AuditLogFilters {
    const next: AuditLogFilters = { ...filters };
    if (isNumberKey(key)) {
        const value = readNumber(key, draft);
        if (value === undefined) delete next[key];
        else next[key] = value;
        return next;
    }
    const value = readText(key, draft);
    if (value === undefined) delete next[key];
    else next[key] = value;
    return next;
}
